import { AdvancedSearchFieldsView } from "./../../views/advanced-search/advanced-search-fields";
import { IAdvancedFormData } from "./../../interfaces/advanced-form-data";
import { NodeType } from "./node-type";

import * as searchfields from "./search-fields.json";

export class TreeNode {

    public children: TreeNode[] = [];
    /** keeps track of how many subsequent children need subsuming */
    private subsequentNodesToSubsume = 0;
    /** Used for debugging only */
    private Id: number;
    /** Set to true if the node was inserted during tree creation rather than supplied by the user */
    private isTransient = false;
    private error: string[] = [];

    /**
     * Construct a TreeNode object. You MUST provide a parentNode unless the node is of type NodeType.RootType
     * @param text text associated with this node
     * @param type NodeType of this node
     * @param parent ParentNode of this node
     */
    constructor(public text: string, public type: NodeType, public parent: TreeNode = null, public currentField: string, transient = false, id = -1) {
        if (!parent && type !== NodeType.Root) {
            throw new Error("No parent supplied for this node, and it is not the Root Node");
        }
        this.Id = id + 1;
        if (type === NodeType.Root) {
            this.text = "";
            transient = true;
        }

        this.isTransient = transient;

    }
    public addError(error: string) {
        if (this.type !== NodeType.Root) {
            this.parent.addError(error); // we only want errors on the root node.
            return;
        }
        this.error.push(error);
    }

    public addChild(child: TreeNode): void {
        if (this.subsequentNodesToSubsume > 0) {
            const subsumer = this.children[this.children.length - 1];
            subsumer.addChild(child);
            child.parent = subsumer;
            this.subsequentNodesToSubsume--;
        } else {
            this.children.push(child);
        }
    }
    /**
     * "Eat" or "subsume" previous child and some amount of future children.
     *  Subsuming means replacing a child with the subsumer, and making the child now a child of the subsumer instead.
     *  @param subsumer the node which will subsume the previous child and next
     *  @param futureNodesToSubsume The number of subsequent nodes to be subsumed. Default 1.
     */
    public subsumeLastChildAndNext(subsumer: TreeNode, futureNodesToSubsume = 1): void {
        const lastIndex = this.children.length - 1;
        const toSubsume = this.children[lastIndex];
        toSubsume.parent = subsumer;
        subsumer.addChild(toSubsume);
        this.children[lastIndex] = subsumer;
        this.subsequentNodesToSubsume = futureNodesToSubsume;
    }

    /** returns a plaintext string representation of the tree as a query */
    public toQueryString(): string {
        return this.internalToString((n) => n.DisplayText);
    }

    public toMarkedUpQueryString(): string {
        return this.internalToString((n) => n.MarkupText);
    }

    /** returns all the terms in the search query that aren't within a specified field */
    public getExtraTerms(): string {
        const x = this.internalToString((n) => n.DisplayText, [NodeType.FieldNameComplete]);
        return x;
    }

    public hasAncestor(ancestorType: NodeType): boolean {
        if (!this.parent) {
            return false;
        }
        if (this.parent.type === ancestorType) {
            return true;
        }
        return this.parent.hasAncestor(ancestorType);
    }

    /**
     * Returns the text value of this node, only if its a non-transient node
     */
    public get DisplayText(): string {
        return (this.isTransient) ? "" : this.text.trim();
    }
    public get MarkupText(): string {
        const currentField = this.toTitleCase(this.currentField); // so either "year" "Year" (or even "yEaR") will have the correct markup

        if (this.DisplayText === "]" || this.DisplayText === "[" || this.type === NodeType.FieldNameComplete) {
            return this.DisplayText; // Square brackets and field names are always just black
        }

        if (!currentField) {
            // currentField = "Anywhere";
            return this.DisplayText;
        }
        return `<span class="search-pill ${currentField}" data-field="${currentField}">${this.DisplayText}</span>`;
    }

    public toTitleCase(str) {
        return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
    }

    /** Detects whether this tree can be represented by breaking it down into search fields */
    public get IsUnrepresentable(): boolean {
        // this.printTreeVerbose();
        const fieldNodes = this.findNodes(NodeType.FieldNameComplete);
        if (fieldNodes.length === 0) { // If there are no fields then the query can be dumped verbatim into "Extra Fields"
            return false;
        }
        const negatedFields = _.any(fieldNodes, (fn) => fn.hasAncestor(NodeType.Not));
        if (negatedFields) { // We can't represent negated fields e.g not year[2000]
            return true;
        }

        const totalOrs = this.findNodes(NodeType.Or).length;
        let orsWithinFields = 0;

        fieldNodes.forEach((f) => orsWithinFields = orsWithinFields + f.findNodes(NodeType.Or).length);

        if (totalOrs > orsWithinFields) { // Ors can't be represented if they are outside fields e.g stack or year[2000]
            return true;
        }

        return false;

    }

    public addChildAndSubsumeNext(child: TreeNode): void {
        this.addChild(child);
        this.subsequentNodesToSubsume = 1;
    }
    /**
     * Get all the terms that appear within a given field. If a field appears multiple times the contents will be joined by an AND
     */
    public getFieldTerms(fieldName: string): string {
        fieldName = fieldName.toLowerCase();
        const fieldNodes = this.findFields(fieldName);
        const results = [];

        fieldNodes.forEach((f) => {
            let res = "";
            f.children[0].children.forEach((c) => res = res + " " + c.toQueryString());
            if (res.charAt(res.length - 1) === "]") {
                res = res.substring(0, res.length - 1 ); // remove the close bracket on the field
            }
            results.push(res);
        });
        return (results.join(this.getFieldConjunctionOperator(fieldName))); // "and" usually, "or" if its year or citation
    }

    /**
     * Get all the field nodes for a given fieldName
     */
    public findFields(fieldName: string): TreeNode[] {
        const allFields = this.findNodes(NodeType.FieldNameComplete);
        return _.filter(allFields, (f) => f.text.toLowerCase() === fieldName.toLowerCase());
    }

    /**
     * Find all nodes of a given type
     * @param desiredType
     * @param retVal Omit this parameter, its used in the recursion to keep track of the current list of found nodes
     */
    public findNodes(desiredType: NodeType, retVal: TreeNode[] = []): TreeNode[] {
        if (this.type === desiredType) {
            retVal.push(this);
        }
        this.children.forEach((c) => c.findNodes(desiredType, retVal));
        return retVal;
    }

    public printTree(depth = "=") {
        if (this.children.length === 0) {
            return;
        }
        this.children.forEach((c) => {
            c.printTree(depth + "=====");
        });
    }

    public get UniqueName() {
        return this.text + "#" + this.Id;
    }

    public get NodeIdentifier() {
        return "Node#" + this.Id;
    }

    public toDataObject(): IAdvancedFormData {

        const data: IAdvancedFormData = {Unrepresentable: this.IsUnrepresentable, Error: this.error };

        const fields = (searchfields as any);

        Object.keys(fields).forEach((key) => {
            data[key] = this.getFieldTerms(fields[key]); // need to cut off the "EU_" bit
        });

        data.Anywhere = this.getExtraTerms();

        return data;
    }
    public getError(): string {
        if (!this.error || this.error.length === 0) {
            return "";
        }
        return this.error[0];
    }
    public get HasError(): boolean {
        if (this.error.length === 0) {
            return false;
        }
        return true;
    }
    public DEBUG_ONLY_printTreeVerbose(depth = "=") {
        // I wanna leave this method (and the dev.log) in as its non-trivial to replicate and will be useful if we run into any tree issues in the future
        dev.log(`${this.isTransient ? depth.replace(/=/g, "~") : depth}I am \"${this.UniqueName}\", my parent is \"${this.parent ? this.parent.UniqueName : "NONE"}\", I have ${this.children.length} children, I am of type ${NodeType[this.type]} and I am within the ${this.currentField} field`);
        this.children.forEach((c) => {
            c.DEBUG_ONLY_printTreeVerbose(depth + "=====");
        });
    }
    /**
     * Get a flat representation of this tree for outputting e.g if we have an AND with children A and B it will return A and B
     * @param nodesToIgnore Optional. A list of nodeTypes to ignore. Ignoring nodetypes will also ignore any children of those nodes
     */
    private resequenceTreeForOutput(nodesToIgnore: NodeType[] = []): TreeNode[] {
        const n2i = nodesToIgnore; // Don't want really long variable names in this method, its ugly enough.

        if (_.contains(n2i, this.type)) { // Should we ignore this nodeType
            return [];
        }

        if (this.type === NodeType.Root || this.type === NodeType.OpenBracket) {
            let meAsArray = ([this] as TreeNode[]);
            this.children.forEach((c) => meAsArray = meAsArray.concat(c.resequenceTreeForOutput(n2i)));
            return meAsArray; // Myself, then all my children in order
        }
        if (this.children.length === 1) { // e.g NOT
            const temp = this.children[0].resequenceTreeForOutput(n2i);
            temp.unshift(this);
            return temp; // Me at the front then my child
        }
        if (this.children.length === 2) { // e.g And/Or
            let temp = this.children[0].resequenceTreeForOutput(n2i); // My first child
            temp.push(this); //                                              Then me
            temp = temp.concat(this.children[1].resequenceTreeForOutput(n2i)); // Then my second child
            return temp;
        }
        if (this.children.length === 3) { // e.g within 10
            let temp = this.children[0].resequenceTreeForOutput(n2i); // First child
            temp.push(this);                                             // Then me
            temp = temp.concat(this.children[1].resequenceTreeForOutput(n2i), // Then 2nd
                               this.children[2].resequenceTreeForOutput(n2i)); // and 3rd children
            return temp;
        }
        if (this.children.length === 4) { // e.g within 10 of
            let temp = this.children[0].resequenceTreeForOutput(n2i); // First child
            temp.push(this);                                            // Then me
            temp = temp.concat(this.children[1].resequenceTreeForOutput(n2i), // Then 2nd
                                this.children[2].resequenceTreeForOutput(n2i), // 3rd
                                this.children[3].resequenceTreeForOutput(n2i)); // and 4 children
            return temp;
        }
        return [this]; // No children? Just return me in an array
    }

    /** returns string representation of the tree/query. Not for external use. Use toQueryString() or toMarkedUpQueryString() instead
     * @param getString a function that takes a TreeNode (this) and gives a string representation of it. Should be using DisplayText or MarkupText accessors
     * @param nodesToIgnore a list of nodetypes to ignore. Ignoring these types will also ignore children of nodes of this type
     */
    private internalToString(getString: (TreeNode) => string, nodesToIgnore: NodeType[] = []): string {
        const ordered = this.resequenceTreeForOutput(nodesToIgnore);
        let queryString = "";
        for (let i = 0; i < ordered.length; i++) {
            let spacer = " ";
            const currentNode = ordered[i];
            const nextNode = ordered[i + 1];
            if (!nextNode) {
                spacer = "";
            } else {
                if (nextNode.type === NodeType.CloseBracket) {
                    spacer = "";
                }
            }
            if (currentNode.type === NodeType.OpenBracket || currentNode.type === NodeType.FieldNameComplete) {
                spacer = "";
            }
            queryString = queryString + getString(currentNode) + spacer;
        }
        return queryString.trim();
        // return this.trimWhitespaceAroundBrackets(this.HTMLify());
    }

    private getFieldConjunctionOperator(fieldName: string): string {
        if (fieldName.toLowerCase() === "year" || fieldName.toLowerCase() === "citation" || fieldName.toLowerCase() === "reference") {
            return "or";
        }
        return "and";
    }

}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./src/search/helpers/search-tree/tree-node.ts